/**
 * MUTAGIM.SHOP — Netflix Product Row (Phase 6)
 * Ligne de produits scrollable style Netflix avec emoji et navigation
 */

import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ProductCardNetflix } from './ProductCardNetflix';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export function NetflixRow({ row, className }) {
  const { i18n } = useTranslation();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const isRTL = i18n.dir() === 'rtl';
  const lang = i18n.language;

  // Obtenir le titre localisé
  const title = row.title?.[lang] || row.title?.fr || 'Produits';
  const emoji = row.emoji || '📦';
  const linkTo = row.linkTo || null;

  // Vérifier les possibilités de scroll
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const effectiveScroll = isRTL ? -scrollLeft : scrollLeft;
      
      setCanScrollLeft(effectiveScroll > 10);
      setCanScrollRight(effectiveScroll < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [row.products]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320 * 2; // 2 cartes
      const currentScroll = scrollRef.current.scrollLeft;
      
      // En RTL, inverser la direction
      const actualDirection = isRTL 
        ? (direction === 'left' ? 'right' : 'left')
        : direction;
      
      const newScroll = actualDirection === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  if (!row.products || row.products.length === 0) return null;

  const hideTitle = row.hideTitle || false;

  return (
    <section 
      className={cn("relative group/row", className)} 
      data-testid={`netflix-row-${row.id}`}
    >
      {/* Header avec titre et emoji */}
      {!hideTitle && (
        <div className="flex items-center justify-between mb-4">
          {linkTo ? (
            <Link to={linkTo} className="group/title">
              <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2 group-hover/title:text-mutagim-red transition-colors">
                <span className="text-2xl">{emoji}</span>
                <span>{title}</span>
                <ChevronRight className="w-5 h-5 opacity-0 group-hover/title:opacity-100 transition-opacity" />
              </h2>
            </Link>
          ) : (
            <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
              <span className="text-2xl">{emoji}</span>
              <span>{title}</span>
            </h2>
          )}
          
          {/* Navigation arrows - Desktop (toujours visibles au hover) */}
          <div className={cn(
            "hidden md:flex items-center gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity",
            "duration-300"
          )}>
            <Button
              variant="outline"
              size="icon"
              className={cn(
                "border-gray-700 text-white hover:bg-gray-800 hover:text-mutagim-red",
                "transition-all",
                !canScrollLeft && "opacity-30 cursor-not-allowed"
              )}
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              data-testid="scroll-left"
            >
            {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "border-gray-700 text-white hover:bg-gray-800 hover:text-mutagim-red",
              "transition-all",
              !canScrollRight && "opacity-30 cursor-not-allowed"
            )}
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            data-testid="scroll-right"
          >
            {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      )}

      {/* Container scrollable */}
      <div className="relative">
        {/* Gradient fade gauche */}
        {canScrollLeft && (
          <div className={cn(
            "absolute top-0 bottom-0 w-16 z-10 pointer-events-none",
            "bg-gradient-to-r from-mutagim-dark to-transparent",
            isRTL ? "right-0" : "left-0"
          )} />
        )}
        
        {/* Gradient fade droite */}
        {canScrollRight && (
          <div className={cn(
            "absolute top-0 bottom-0 w-16 z-10 pointer-events-none",
            "bg-gradient-to-l from-mutagim-dark to-transparent",
            isRTL ? "left-0" : "right-0"
          )} />
        )}

        {/* Scrollable row */}
        <div 
          ref={scrollRef}
          className={cn(
            "flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4",
            isRTL && "flex-row-reverse"
          )}
          style={{ 
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {row.products.map((product, index) => (
            <div 
              key={product.id || product.slug || index}
              className="flex-shrink-0 w-[180px] md:w-[220px] lg:w-[240px]"
              style={{ scrollSnapAlign: 'start' }}
            >
              <ProductCardNetflix product={product} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default NetflixRow;
